import { Typography } from '@/components/atoms/Typography';
import { UnitReservedConfettiIcon } from '@/components/atoms/icons';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import React, { useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { ContentDialog } from './ContentDialog';

export interface UnitReservedDialogProps {
    visible: boolean;
    onOk: () => void;
}

/**
 * Success dialog shown after "Proceed to Reserve" — mobile counterpart of
 * properties-web's UnitReservedSuccessDialog (same confetti icon + copy).
 */
export function UnitReservedDialog({ visible, onOk }: Readonly<UnitReservedDialogProps>) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const viewTrackedRef = useRef(false);

    useEffect(() => {
        if (!visible) {
            viewTrackedRef.current = false;
            return;
        }
        if (viewTrackedRef.current) {
            return;
        }
        viewTrackedRef.current = true;
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.RESERVE,
            actionName: ALMETLAA_ACTION.RESERVE.UNIT_RESERVED_DIALOG_VIEW,
            eventType: 'view',
        });
    }, [visible]);

    return (
        <ContentDialog
            visible={visible}
            onClose={onOk}
            contentAlign="center"
            maxWidth={340}
            icon={<UnitReservedConfettiIcon size={80} />}
            title={t('reserveUnit.unitReservedTitle')}
            titleVariant="title3"
            titleColor="primary"
            titleStyle={styles.title}
            actions={[
                {
                    label: t('common.ok'),
                    variant: 'primary',
                    onPress: () => {
                        trackCatalogEvent({
                            screen: ALMETLAA_SCREEN.RESERVE,
                            actionName: ALMETLAA_ACTION.RESERVE.UNIT_RESERVED_SUCCESS_PRIMARY_CLICK,
                            eventType: 'action',
                        });
                        onOk();
                    },
                    flex: false,
                },
            ]}
        >
            <View style={styles.body}>
                <Typography variant="body" color={colors.successNoteTitle} style={styles.bodyText}>
                    {t('reserveUnit.unitReservedCongrats')}
                </Typography>
                <Typography variant="body" color="secondary" style={styles.bodyText}>
                    {t('reserveUnit.unitReservedMessage')}
                </Typography>
            </View>
        </ContentDialog>
    );
}

const styles = StyleSheet.create({
    title: {
        lineHeight: 20,
        textAlign: 'center',
    },
    body: {
        alignItems: 'center',
        gap: 8,
        width: '100%',
    },
    bodyText: {
        lineHeight: 22,
        textAlign: 'center',
    },
});

export default UnitReservedDialog;
